import React, { useState, useEffect, ReactNode, useMemo, useRef } from "react";
import { useAuth } from "../../../firebase/AuthProvider";
import { Conversation } from "../../../services/chatService/ChatService";
import GlobalChatInput, { GlobalChatInputRef } from "./GlobalChatInput";
import ChatHeader from "./ChatHeader";
import MessageList from "./MessageList";
import EmojiPicker from "../emojiPicker/EmojiPicker";
import sendmessageicon from "../../../assets/images/sendmessageicon.svg";
import LoadingSpinner from "../../ui/Loading/LoadingSpinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFile } from "@fortawesome/free-solid-svg-icons";
import { useGlobalChat } from "../../../hooks/globalChatHooks/useGlobalChat";
import "./GlobalChatInput.css";
import { ValidateOtpResponseDto } from "../../../interfaces/ExtermalUserDtos/ExternalUsersDtos";
import { currentConversationId$ } from "../../../services/Subjects/userSubject";
import { formatFileSize } from "../../../utilities/fileUtils";
import pdficon from "../../../assets/images/pdficon.svg";
import excelicon from "../../../assets/images/excelicon.svg";
import wordfileicon from "../../../assets/images/wordfileicon.svg";
import trashicon from "../../../assets/images/trashicons.svg";
import MeetingBanner from "./meetingBanner/MeetingBanner";
import { useNavigate } from "react-router-dom";
import { GetExistingOrCretateMeetingAsync } from "../../../services/videoService/VideoService"; // Import this
import { LobbyState } from "../../../interfaces/videocallDtos/videoCall"; // Import LobbyState
import { toast } from "react-toastify"; // Import toast
import { useChatData } from "../../../hooks/taskChatHooks/useChatData";
import { LuSend } from "react-icons/lu";
import { LuSmile } from "react-icons/lu";


const Send = LuSend as any;
const Smile = LuSmile as any;

interface GlobalChatWindowProps {
    conversation: Conversation;
    handleChatClose?: () => void;
    singlePane?: boolean;
    isShowCallingIcon?: boolean;
    externalUserDetails?: ValidateOtpResponseDto | null;
    scrollToBottomTrigger?: boolean;
}

const extensionToIcon: { [key: string]: string } = {
    '.pdf': pdficon,
    '.xls': excelicon,
    '.xlsx': excelicon,
    '.doc': wordfileicon,
    '.docx': wordfileicon,
};
const GlobalChatWindow: React.FC<GlobalChatWindowProps> = ({
    conversation,
    handleChatClose,
    singlePane,
    isShowCallingIcon = true,
    externalUserDetails = null,
    scrollToBottomTrigger
}) => {
    const { currentUser } = useAuth();
    const { users: allUsers } = useChatData(); //
    const chatInputRef = useRef<GlobalChatInputRef>(null);
    const navigate = useNavigate();
    const {
        messages,
        isLoading,
        sendMessage,
        sendReaction,
        typingUsers,
        sendTypingStatus,
        markAsRead,
        hasMore,
        loadingMore,
        loadMoreMessages,
    } = useGlobalChat(conversation.id, externalUserDetails);
    const sendSound = useMemo(() => new Audio('/sounds/message-sent.mp3'), []);
    const isSendingRef = useRef(false);
    const [joinLoading, setJoinLoading] = useState(false); // State for join button loading

    useEffect(() => {
        if (conversation.id) {
            markAsRead();
        }
    }, [conversation.id, markAsRead]);

    const [commentText, setCommentText] = useState("");
    const [selectedFile, setSelectedFile] = useState<File>();
    const [filePreview, setFilePreview] = useState<{
        url: string;
        icon?: ReactNode;
    } | null>(null);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [isSending, setIsSending] = useState(false);

    const otherParticipants = conversation.participants.filter(
        (p) => p.id !== currentUser?.uid || externalUserDetails?.externalUserDetailId
    );
    const typingDisplayNames = typingUsers
        .map((id) => otherParticipants.find((p) => p.id === id)?.displayName)
        .filter(Boolean);

    const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleTextChange = (text: string) => {
        setCommentText(text);

        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        } else {
            sendTypingStatus(true);
        }

        typingTimeoutRef.current = setTimeout(() => {
            sendTypingStatus(false);
            typingTimeoutRef.current = null;
        }, 3000);
    };

    useEffect(() => {
        return () => {
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
                sendTypingStatus(false);
            }
        };
    }, [sendTypingStatus]);

    useEffect(() => {
        return () => {
            currentConversationId$.next(0);
        };
    }, []);


    useEffect(() => {
        if (!selectedFile) {
            setFilePreview(null);
            return;
        }

        const mime = selectedFile.type.toLowerCase();
        if (mime.startsWith("image/")) {
            const blobUrl = URL.createObjectURL(selectedFile);
            setFilePreview({ url: blobUrl });
            return () => URL.revokeObjectURL(blobUrl);
        } else {
            const ext = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
            const iconSrc = extensionToIcon[ext];
            let icon: ReactNode;
            if (iconSrc) {
                icon = <img src={iconSrc} alt="file icon" className="w-8 h-8 object-contain" />;
            } else {
                icon = <FontAwesomeIcon icon={faFile} className="text-gray-500 text-3xl" />;
            }
            setFilePreview({ url: "", icon: icon });
        }
    }, [selectedFile]);


    const handleSubmit = async () => {
        if (isSendingRef.current) return;
        if (!commentText.trim() && !selectedFile) return;

        isSendingRef.current = true;
        setIsSending(true);

        sendSound.play().catch((e) => console.error("Error playing sound:", e));
        try {
            await sendMessage(commentText, selectedFile);
            setCommentText("");
            setSelectedFile(undefined);
        } finally {
            isSendingRef.current = false;
            setIsSending(false);
        }
    };

    useEffect(() => {
        setCommentText("");
        setSelectedFile(undefined);
    }, [conversation.id]);

    // --- Helper to Force UTC Interpretation ---
    const getEventDate = (dateString?: string | Date): Date => {
        if (!dateString) return new Date();
        if (dateString instanceof Date) return dateString;
        if (typeof dateString === 'string' && !dateString.endsWith('Z') && dateString.includes('T')) {
            return new Date(dateString + 'Z');
        }
        return new Date(dateString);
    };

    // --- Meeting Banner Logic ---
    const activeMeeting = conversation.activeMeeting;
    const rawTargetDate = activeMeeting?.startDate || activeMeeting?.createdAt;
    const meetingTargetDate = getEventDate(rawTargetDate);
    const meetingTitle = activeMeeting?.title || "Meeting";

    // --- Join Logic ---
    const handleJoinMeeting = async () => {
        if (!activeMeeting || joinLoading) return;

        setJoinLoading(true);
        try {
            const token = await currentUser?.getIdToken();
            if (!token) throw new Error("No auth token");

            const entityId = activeMeeting.calendarEventId || conversation.id.toString();

            // For now, mirroring RescheduleMeetingSection logic:
            const res = await GetExistingOrCretateMeetingAsync(token, activeMeeting.calendarEventId || "", conversation.id.toString());

            if (res.success) {
                const states: LobbyState = {
                    calenderEventId: activeMeeting.calendarEventId || undefined,
                    // conversationId: !activeMeeting.calendarEventId ? conversation.id.toString() : undefined, // Pass conversationId if no calendar event
                    isPublisher: res.data.isPublisher,
                    isOneToOneCall: false,
                };

                navigate(`/lobby/${res.data.roomId}`, { state: states });
            } else {
                toast.error(res.message);
            }
        } catch (error: any) {
            console.error("Join meeting error:", error);
            toast.error(error.message || "Failed to join meeting");
        } finally {
            setJoinLoading(false);
        }
    };

    return (
        <div className="chat_body p-0 m-0 border-none">
            <ChatHeader
                conversation={conversation}
                handleChatClose={handleChatClose}
                singlePane={singlePane}
                isShowCallingIcon={isShowCallingIcon}
            />

            {/* Conditionally Render Meeting Banner */}
            {activeMeeting && (
                <MeetingBanner
                    targetDate={meetingTargetDate}
                    meetingUrl="" // Empty string prevents window.open
                    title={meetingTitle}
                    onJoinClick={handleJoinMeeting}
                />
            )}

            {/* Show global loader if joining */}
            {joinLoading && <LoadingSpinner text={false} />}

            {isLoading ? (
                <div className="flex items-center justify-center h-full">
                    <LoadingSpinner text={false} w={6} h={6} />
                </div>
            ) : (
                <MessageList
                    messages={messages}
                    currentUserId={currentUser ? currentUser.uid : externalUserDetails!.externalUserDetailId}
                    onAddReaction={sendReaction}
                    externalUserDetails={externalUserDetails}
                    onLoadMore={loadMoreMessages}
                    loadingTop={loadingMore}
                    hasMore={hasMore}
                    scrollToBottomTrigger={scrollToBottomTrigger}
                />
            )}

            <div className="chat_footer">
                <div className="typing-indicator-container h-6 px-4 text-sm text-gray-500 italic">
                    {typingDisplayNames.length > 0 && (
                        <span>
                            {typingDisplayNames.join(", ")}
                            {typingDisplayNames.length === 1
                                ? " is typing..."
                                : " are typing..."}
                        </span>
                    )}
                </div>
                {selectedFile && filePreview && (
                    <div className="file-preview p-3 rounded-lg mx-2 mb-2 bg-gray-50 border border-gray-200">
                        <div className="flex items-center gap-3">
                            {filePreview.icon ? (
                                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-white rounded-lg">
                                    {filePreview.icon}
                                </div>
                            ) : (
                                <img
                                    src={filePreview.url}
                                    alt="preview"
                                    className="w-10 h-10 object-cover rounded-lg"
                                />
                            )}
                            <div className="flex-1 overflow-hidden">
                                <p className="truncate text-sm font-medium text-gray-800">{selectedFile.name}</p>
                                <p className="text-xs text-gray-500">
                                    {formatFileSize(selectedFile.size)}
                                </p>
                            </div>
                            <button
                                onClick={() => setSelectedFile(undefined)}
                                className="text-gray-500 hover:text-red-500 p-1"
                            >
                                <img src={trashicon} alt="remove" className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                )}

                {
                    (
                        !conversation.channel?.channelId
                        || (conversation.channel?.channelId && conversation.channel.ownerId === currentUser?.uid)
                    ) && (
                        <div className="flex items-end pb-[20px]">
                            <div className="w-full mx-[15px] bg-[#F9FAFB] rounded-[10px] px-[15px] pt-[11px] pb-[30px]" style={{ border: "#E5E7EB 1px solid" }}>
                                {/* <GlobalChatInput
                                ref={chatInputRef}
                                commentText={commentText}
                                onTextChange={handleTextChange}
                                onSelectFile={setSelectedFile}
                                onSendMessage={handleSubmit}
                            /> */}

                                <GlobalChatInput
                                    ref={chatInputRef}
                                    commentText={commentText}
                                    onTextChange={handleTextChange}
                                    onSelectFile={setSelectedFile}
                                    onSendMessage={handleSubmit}
                                    users={allUsers ?? []} // Use ALL users instead of conversation.participants
                                />

                                <div className="relative">
                                    <button
                                        className="absolute left-7"
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                    >
                                        <Smile className="text-[#6A7282] w-5 h-5" />{""}
                                    </button>
                                    {showEmojiPicker && (
                                        <div className="absolute bottom-14">
                                            <EmojiPicker
                                                onSelect={(emoji) => {
                                                    chatInputRef.current?.insertEmoji(emoji);
                                                    setShowEmojiPicker(false);
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>

                            </div>
                            <div className="pb-1 shrink-0 w-fit pr-[18px]">
                                <button
                                    className="disabled:bg-[#F5F3FF] bg-[#7e57c2] rounded-[10px] p-2 w-10 h-10 flex items-center justify-center text-white
                                    shadow-[0px_0.94px_1.87px_-0.94px_rgba(0,0,0,0.1),_0px_0.94px_2.81px_0px_rgba(0,0,0,0.1)]"
                                    onClick={handleSubmit}
                                    disabled={isSending || (!commentText.trim() && !selectedFile)}
                                >
                                    {isSending ? (
                                        <LoadingSpinner text={false} w={4} h={4} />
                                    ) : (
                                        <Send className="text-lg" />
                                    )}
                                </button>
                            </div>
                        </div>
                    )
                }
            </div>
        </div>
    );
};

export default GlobalChatWindow;